import {
  useCallback,
  useRef,
  type MouseEvent,
  type RefObject,
  type TouchEvent,
  type WheelEvent,
} from 'react';

import type {
  LayoutPoint,
  ViewerEntityId,
  ViewerPosition,
} from '../contracts/models';
import {
  distanceBetweenLayoutPoints,
  normalizeAreaTagPolygon,
  type AreaTagDrawingPolygon,
  type AreaTagDrawingSnap,
  type DrawingLineAssist,
} from '../geometry/areaTagDrawing';
import { calculatePolygonCentroid } from '../geometry/polygons';
import { clientToContentPoint } from '../geometry/contentCoordinates';
import { useAreaTagDrawing } from '../hooks/useAreaTagDrawing';

export interface AreaTagDrawingGuide<
  TId extends ViewerEntityId = ViewerEntityId,
> extends AreaTagDrawingPolygon<TId> {
  name?: string;
}

export interface AreaTagDrawingLayerProps<
  TId extends ViewerEntityId = ViewerEntityId,
> {
  guides: readonly AreaTagDrawingGuide<TId>[];
  points: readonly LayoutPoint[];
  hoverPoint: LayoutPoint | null;
  snap: AreaTagDrawingSnap<TId> | null;
  horizontalConstrained: boolean;
  verticalConstrained: boolean;
  parallelAssist: DrawingLineAssist | null;
  equalLengthAssist: DrawingLineAssist | null;
  closeThreshold?: number;
}

export const AreaTagDrawingLayer = <
  TId extends ViewerEntityId = ViewerEntityId,
>({
  guides,
  points,
  hoverPoint,
  snap,
  horizontalConstrained,
  verticalConstrained,
  parallelAssist,
  equalLengthAssist,
  closeThreshold = 20,
}: AreaTagDrawingLayerProps<TId>) => {
  const previewPoint = snap?.point ?? hoverPoint;
  const firstPoint = points[0];
  const lastPoint = points[points.length - 1];
  const canClose = Boolean(
    points.length >= 3 &&
      firstPoint &&
      previewPoint &&
      distanceBetweenLayoutPoints(firstPoint, previewPoint) <
        closeThreshold,
  );

  return (
    <g className="iv-area-tag-drawing-layer">
      {guides.map((guide) => {
        const guidePoints = normalizeAreaTagPolygon(guide.points);
        if (guidePoints.length < 2) return null;
        const centroid = calculatePolygonCentroid(guidePoints);
        return (
          <g key={String(guide.id)}>
            <polygon
              points={guidePoints
                .map(([x, y]) => `${x},${y}`)
                .join(' ')}
              fill="rgba(34, 197, 94, 0.08)"
              stroke="#22c55e"
              strokeWidth="2"
              strokeDasharray="8,4"
              opacity="0.7"
              pointerEvents="none"
            />
            {guidePoints.map(([x, y], index) => (
              <g key={`${x}:${y}:${index}`}>
                <circle
                  cx={x}
                  cy={y}
                  r="6"
                  fill="rgba(34, 197, 94, 0.3)"
                  pointerEvents="none"
                />
                <circle
                  cx={x}
                  cy={y}
                  r="3"
                  fill="#22c55e"
                  stroke="white"
                  strokeWidth="2"
                  pointerEvents="none"
                />
              </g>
            ))}
            {guide.name && guidePoints.length >= 3 ? (
              <text
                x={centroid.x}
                y={centroid.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#22c55e"
                stroke="white"
                strokeWidth="3"
                paintOrder="stroke"
                fontSize="12"
                fontWeight="500"
                opacity="0.8"
                pointerEvents="none"
              >
                {guide.name}
              </text>
            ) : null}
          </g>
        );
      })}

      {parallelAssist &&
      points[parallelAssist.referenceLineIndex + 1] ? (
        <line
          x1={points[parallelAssist.referenceLineIndex][0]}
          y1={points[parallelAssist.referenceLineIndex][1]}
          x2={points[parallelAssist.referenceLineIndex + 1][0]}
          y2={points[parallelAssist.referenceLineIndex + 1][1]}
          stroke="#f59e0b"
          strokeWidth="4"
          opacity="0.7"
          pointerEvents="none"
        />
      ) : null}
      {equalLengthAssist &&
      points[equalLengthAssist.referenceLineIndex + 1] ? (
        <line
          x1={points[equalLengthAssist.referenceLineIndex][0]}
          y1={points[equalLengthAssist.referenceLineIndex][1]}
          x2={points[equalLengthAssist.referenceLineIndex + 1][0]}
          y2={points[equalLengthAssist.referenceLineIndex + 1][1]}
          stroke="#8b5cf6"
          strokeWidth="4"
          opacity="0.7"
          pointerEvents="none"
        />
      ) : null}

      {points.map((point, index) => {
        const previous = points[index - 1];
        return (
          <g key={`${point[0]}:${point[1]}:${index}`}>
            {previous ? (
              <>
                <line
                  x1={previous[0]}
                  y1={previous[1]}
                  x2={point[0]}
                  y2={point[1]}
                  stroke="white"
                  strokeWidth="6"
                  opacity="0.8"
                  pointerEvents="none"
                />
                <line
                  x1={previous[0]}
                  y1={previous[1]}
                  x2={point[0]}
                  y2={point[1]}
                  stroke="#3b82f6"
                  strokeWidth="3"
                  strokeDasharray="8,4"
                  pointerEvents="none"
                />
              </>
            ) : null}
            <circle
              cx={point[0]}
              cy={point[1]}
              r="10"
              fill={
                index === 0
                  ? 'rgba(34, 197, 94, 0.3)'
                  : 'rgba(59, 130, 246, 0.3)'
              }
              pointerEvents="none"
            />
            <circle
              cx={point[0]}
              cy={point[1]}
              r="6"
              fill={index === 0 ? '#22c55e' : '#3b82f6'}
              stroke="white"
              strokeWidth="3"
              pointerEvents="none"
            />
          </g>
        );
      })}

      {lastPoint && previewPoint ? (
        <>
          <line
            x1={lastPoint[0]}
            y1={lastPoint[1]}
            x2={canClose && firstPoint ? firstPoint[0] : previewPoint[0]}
            y2={canClose && firstPoint ? firstPoint[1] : previewPoint[1]}
            stroke="white"
            strokeWidth="4"
            opacity="0.7"
            pointerEvents="none"
          />
          <line
            x1={lastPoint[0]}
            y1={lastPoint[1]}
            x2={canClose && firstPoint ? firstPoint[0] : previewPoint[0]}
            y2={canClose && firstPoint ? firstPoint[1] : previewPoint[1]}
            stroke={canClose ? '#22c55e' : '#3b82f6'}
            strokeWidth={canClose ? 3 : 2}
            strokeDasharray={canClose ? '6,3' : '4,4'}
            opacity="0.8"
            pointerEvents="none"
          />
        </>
      ) : null}

      {firstPoint && points.length >= 3 ? (
        <circle
          cx={firstPoint[0]}
          cy={firstPoint[1]}
          r="15"
          fill="none"
          stroke="#22c55e"
          strokeWidth="3"
          strokeDasharray="6,3"
          opacity={canClose ? 1 : 0.6}
          pointerEvents="none"
        />
      ) : null}

      {snap ? (
        <g pointerEvents="none">
          <circle
            cx={snap.point[0]}
            cy={snap.point[1]}
            r="15"
            fill="none"
            stroke={snap.type === 'vertex' ? '#f97316' : '#eab308'}
            strokeWidth="2"
            opacity="0.7"
          >
            <animate
              attributeName="r"
              values="12;18;12"
              dur="1.5s"
              repeatCount="indefinite"
            />
          </circle>
          <circle
            cx={snap.point[0]}
            cy={snap.point[1]}
            r="8"
            fill={snap.type === 'vertex' ? '#f97316' : '#eab308'}
            stroke="white"
            strokeWidth="3"
            opacity="0.9"
          />
        </g>
      ) : hoverPoint ? (
        <circle
          cx={hoverPoint[0]}
          cy={hoverPoint[1]}
          r="4"
          fill="#3b82f6"
          stroke="white"
          strokeWidth="2"
          opacity="0.8"
          pointerEvents="none"
        />
      ) : null}

      {lastPoint &&
      hoverPoint &&
      (horizontalConstrained ||
        verticalConstrained ||
        parallelAssist ||
        equalLengthAssist) ? (
        <text
          x={hoverPoint[0] + 10}
          y={hoverPoint[1] - 10}
          fill={equalLengthAssist ? '#8b5cf6' : '#f59e0b'}
          stroke="white"
          strokeWidth="2"
          paintOrder="stroke"
          fontSize="12"
          fontWeight="500"
          pointerEvents="none"
        >
          {horizontalConstrained
            ? 'H'
            : verticalConstrained
              ? 'V'
              : parallelAssist
                ? '∥ Parallel'
                : '≈ Same Length'}
        </text>
      ) : null}
    </g>
  );
};

export interface AreaTagDrawingOverlayProps<
  TId extends ViewerEntityId = ViewerEntityId,
> {
  active: boolean;
  isPdfLayout?: boolean;
  dimensions: { width: number; height: number };
  position: ViewerPosition;
  scale: number;
  guides: readonly AreaTagDrawingGuide<TId>[];
  onComplete(points: readonly LayoutPoint[]): void;
  onWheel?: (event: WheelEvent<SVGSVGElement>) => void;
  onTouchStart?: (event: TouchEvent<SVGSVGElement>) => void;
  onTouchMove?: (event: TouchEvent<SVGSVGElement>) => void;
  onTouchEnd?: (event: TouchEvent<SVGSVGElement>) => void;
  svgRef?: RefObject<SVGSVGElement>;
  groupRef?: RefObject<SVGGElement>;
}

export const AreaTagDrawingOverlay = <
  TId extends ViewerEntityId = ViewerEntityId,
>({
  active,
  isPdfLayout = false,
  dimensions,
  position,
  scale,
  guides,
  onComplete,
  onWheel,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  svgRef: providedSvgRef,
  groupRef: providedGroupRef,
}: AreaTagDrawingOverlayProps<TId>) => {
  const internalSvgRef = useRef<SVGSVGElement>(null);
  const internalGroupRef = useRef<SVGGElement>(null);
  const svgRef = providedSvgRef ?? internalSvgRef;
  const groupRef = providedGroupRef ?? internalGroupRef;
  const getContentPoint = useCallback(
    (event: { clientX: number; clientY: number }) => {
      if (!svgRef.current) return null;
      return clientToContentPoint(
        svgRef.current,
        event.clientX,
        event.clientY,
        { isPdfLayout, position, scale },
      );
    },
    [isPdfLayout, position, scale, svgRef],
  );
  const drawing = useAreaTagDrawing({
    enabled: active,
    polygons: guides,
    scale,
    getContentPoint,
    onComplete,
  });

  if (!active) return null;

  return (
    <div
      className="iv-area-tag-drawing-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'auto',
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={
          isPdfLayout
            ? undefined
            : `-40 -40 ${dimensions.width + 80} ${dimensions.height + 80}`
        }
        preserveAspectRatio="xMidYMid meet"
        onClick={(event: MouseEvent<SVGSVGElement>) =>
          drawing.handlePointerClick(event)
        }
        onMouseMove={(event: MouseEvent<SVGSVGElement>) =>
          drawing.handlePointerMove(event)
        }
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          cursor: 'crosshair',
          touchAction: 'none',
          background: 'transparent',
        }}
      >
        <g
          ref={groupRef}
          transform={`translate(${position.x}, ${position.y}) scale(${scale})`}
        >
          <AreaTagDrawingLayer guides={guides} {...drawing} />
        </g>
      </svg>
    </div>
  );
};
